import { CardSubWrapper, CardWrapper } from 'admin/components/wrappers';
import TeamCell from 'app/components/CustomDataGrid/Cells/TeamCell';
import CustomDataGrid from 'app/components/CustomDataGrid/Index';
import { CustomDeletePopup } from 'app/components/CustomDeletePopup';
import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import {
  GeneralLookup,
  CoachListWrapper,
  ChangeCoachActiveStatusRequest,
} from 'services/openapi';
import { useNavigateToRoute } from 'utils/common';
import {
  GridColumnProps,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid';
import AdminCoachProfileCell from 'app/components/CustomDataGrid/Cells/AdminCoachProfileCell';
import CoachAddressCell from 'app/components/CustomDataGrid/Cells/CoachAddressCell';
import CoachAffiliationCell from 'app/components/CustomDataGrid/Cells/CoachAffiliationCell';
import CoachphoneCell from 'app/components/CustomDataGrid/Cells/CoachPhoneCell';
import CoachStatus from 'app/components/CustomDataGrid/Cells/CoachStatus';
import { useLocation } from 'react-router-dom';
import { SortDescriptor } from '@progress/kendo-data-query';
import { TableSkeletonLoader } from 'app/components/TableSkeletonLoader';
import HorizontalKebabCell from 'app/components/CustomDataGrid/Cells/HorizontalKebabCell';
import { usePermission } from 'hooks/usePermission';
type Props = {
  coachProfileData?: CoachListWrapper;
  setPageSkip: React.Dispatch<React.SetStateAction<number | undefined>>;
  pageSkip: number | undefined;
  debounceCoachSearch: string;
  selectedItemId: string | null;
  lookup: GeneralLookup[];
  pageSize: number;
  fetchManageGetCoaches: (
    season?: string | undefined,
    teamFilter?: Array<string>,
    searchInput?: string | undefined,
    status?: string | undefined,
    sortBy?: string | undefined,
    sortOrder?: number | undefined,
    pageNumber?: number,
    pageSize?: number,
    paidFilter?: number,
  ) => void;
  CoachActiveOrInactive: (
    coachId: string,
    season?: string | undefined,
    teamFilter?: Array<string>,
    requestBody?: ChangeCoachActiveStatusRequest,
    searchInput?: string,
    status?: string,
    sortBy?: string,
    sortOrder?: number,
    pageNumber?: number,
    pageSize?: number,
    item?: string,
  ) => void;
  deleteCoachList: (
    id?: string | undefined,
    season?: string | undefined,
    teamFilter?: Array<string>,
    searchInput?: string | undefined,
    status?: string | undefined,
    sortBy?: string | undefined,
    sortOrder?: number | undefined,
    pageNumber?: number,
    pageSize?: number,
  ) => void;
  sortField: string | undefined;
  sortOrder: number | undefined;
  setSortField: React.Dispatch<React.SetStateAction<string>>;
  setSortOrder: React.Dispatch<React.SetStateAction<number>>;
  loading: boolean;
  teamId: string[];
  paidFilter?: number | null;
  pageSizeSelected: GeneralLookup;
};
const CoachesTable = ({
  coachProfileData,
  setPageSkip,
  pageSkip,
  debounceCoachSearch,
  fetchManageGetCoaches,
  selectedItemId,
  deleteCoachList,
  lookup,
  CoachActiveOrInactive,
  sortField,
  sortOrder,
  setSortField,
  setSortOrder,
  loading,
  teamId,
  paidFilter,
  pageSize,
  pageSizeSelected
}: Props) => {
  const redirect = useNavigateToRoute();
  const { hasViewPermission } = usePermission();
  const currentLocation = useLocation();
  const isPastDataFlag = currentLocation.pathname.includes('past-data');
  const activeId = lookup.find(item => item.name === 'Active');
  const inActiveId = lookup.find(item => item.name === 'Inactive');
  const [coachId, setCoachId] = useState('');
  const [showDeletePopup, setShowDeletePopup] = React.useState<boolean>(false);
  const initialSort: Array<SortDescriptor> = useMemo(
    () => [{ field: 'coachName', dir: 'asc' }],
    [],
  );
  const [sort, setSort] = React.useState(initialSort);
  React.useEffect(() => {
    setSort(initialSort);
  }, [pageSize, initialSort]);
  const handleMoreClick = (item: string, e) => {
    const seasonId = e.dataItem.season.id;
    let activeRequestBody = {
      statusId: activeId?.id ?? '',
      seasonId: seasonId,
    };
    let inActiveRequestBody = {
      statusId: inActiveId?.id ?? '',
      seasonId: seasonId,
    };
    const id = e.dataItem.id;
    if (item === 'Make active') {
      if (hasViewPermission('0400101005')) {
        CoachActiveOrInactive(
          id,
          e.dataItem.season?.id,
          teamId,
          activeRequestBody,
          debounceCoachSearch,
          selectedItemId ?? '',
          sortField,
          sortOrder,
          pageSkip,
          pageSize,
          item,
        );
      }
    } else if (item === 'Make inactive') {
      if (hasViewPermission('0400101005')) {
        CoachActiveOrInactive(
          id,
          e.dataItem.season?.id,
          teamId,
          inActiveRequestBody,
          debounceCoachSearch,
          selectedItemId ?? '',
          sortField,
          sortOrder,
          pageSkip,
          pageSize,
          item,
        );
      }
    } else if (item === 'Delete coach') {
      if (hasViewPermission('0400101006')) {
        setCoachId(id);
        setShowDeletePopup(true);
      }
    } else if (item === 'Edit coach info') {
      if (hasViewPermission('0400101005')) {
        redirect(`/admin/coaches/add/update/${id}`);
      }
    }
  };
  const handleViewClick = e => {
    if (isPastDataFlag) {
      redirect(`/admin/past-data/coaches/view/${e.id}`);
    } else {
      if (hasViewPermission('0400101003')) {
        redirect(`/admin/coaches/view/${e.id}`);
      }
    }
  };
  const CoachColumn: GridColumnProps[] = [
    ...(hasViewPermission('0400101005') || hasViewPermission('0400101006')
      ? [
        {
          field: 'action',
          title: 'Action',
          cell: e => (
            <HorizontalKebabCell
              isAssigned={true}
              e={e}
              onMoreClick={handleMoreClick}
              isCoach={true}
              isPastDataFlag={isPastDataFlag}
            />
          ),
          width: `${isPastDataFlag ? '0' : '100'}`,
          sortable: false,
          locked: true,
        },
      ]
      : []),

    {
      field: 'coachName',
      title: 'Coach Name',
      cell: e => (
        <AdminCoachProfileCell e={e} onViewClick={e => handleViewClick(e)} />
      ),
      width: '250',
      sortable: true,
      locked: true,
      className: 'coach-name-style',
      headerClassName: 'coach-header-style',
    },
    {
      field: 'team',
      title: 'Team',
      cell: e => <TeamCell e={e} />,
      width: '180',
      sortable: true,
      headerClassName: 'team-header-style',
    },
    {
      field: 'address',
      title: 'Address',
      cell: e => <CoachAddressCell e={e} />,
      width: '180',
      sortable: false,
    },
    // {
    //   field: 'email',
    //   title: 'Email',
    //   cell: e => <EmailCell e={e} />,
    //   width: '200',
    //   sortable: false,
    // },
    {
      field: 'primaryPhone',
      title: 'Phone',
      cell: e => <CoachphoneCell e={e} />,
      width: '120',
      sortable: false,
    },
    {
      field: 'affiliations',
      title: 'Affiliations',
      cell: e => <CoachAffiliationCell e={e} />,
      width: '200',
      sortable: false,
    },
    // {
    //   field: '',
    //   title: 'Extras',
    //   cell: e => <ExtrasCell e={e} />,
    //   width: '250',
    //   sortable: false,
    // },
    // {
    //   field: 'isPaymentComplete',
    //   title: 'Payment Completed',
    //   cell: e => <PaymentStatusCell e={e} />,
    //   width: '150',
    //   sortable: false,
    // },
    {
      field: 'status',
      title: 'Status',
      cell: e => <CoachStatus e={e} />,
      width: '100',
      sortable: false,
    },
  ];
  const CoachPastDataColumn = CoachColumn.slice(0, -1);
  const handlePageChange = event => {
    setPageSkip(event.page.skip);
    fetchManageGetCoaches(
      '',
      teamId,
      debounceCoachSearch,
      selectedItemId ?? '',
      sortField,
      sortOrder,
      event.page.skip,
      pageSize,
      paidFilter as number,
    );
  };
  const handleDeleteCoachList = () => {
    const currentItemCount = coachProfileData?.coaches?.length ?? 0;

    // Calculate next page skip if we're deleting the last item on this page
    let nextPageSkip = pageSkip;
    if (currentItemCount <= 1 && pageSkip && pageSkip > 0) {
      nextPageSkip = pageSkip - pageSize;
      setPageSkip(nextPageSkip);
    }

    deleteCoachList(
      coachId,
      '',
      teamId,
      debounceCoachSearch,
      selectedItemId ?? '',
      sortField,
      sortOrder,
      nextPageSkip ?? 0,
      pageSize,
    );
    setShowDeletePopup(false);
  };
  const handleSortChange = e => {
    let sortOrderValue = e.dir === 'asc' ? 0 : 1;
    setSortOrder(sortOrderValue);
    setSortField(e.field);
    fetchManageGetCoaches(
      '',
      teamId,
      debounceCoachSearch,
      selectedItemId ?? '',
      e.field,
      sortOrderValue,
      pageSkip,
      pageSize,
      paidFilter as number,
    );
  };

  return (
    <>
      {loading ? (
        <TableSkeletonLoader rowContent={8} width={100} headingWidth={80} />
      ) : (
        <CardWrapper style={{ paddingTop: '15px' }}>
          <CardSubWrapper className="d-flex"></CardSubWrapper>
          <GridWrapper>
            <CustomDataGrid
              showPagination={(coachProfileData?.pageDetails?.count ?? 0) > Number(pageSizeSelected.id)}
              data={coachProfileData?.coaches ?? []}
              columns={isPastDataFlag ? CoachPastDataColumn : CoachColumn}
              pageSize={pageSize}
              totalCount={coachProfileData?.pageDetails?.count}
              onPageChange={event => {
                handlePageChange(event);
              }}
              skip={pageSkip}
              sortable={{
                allowUnsort: false,
                mode: 'single',
              }}
              sort={sort}
              onSortChange={(e: GridSortChangeEvent) => {
                let sortvalue = e.sort[0];
                setSort(e.sort);
                handleSortChange(sortvalue);
              }}
            />
            <CustomDeletePopup
              deleteIcon={true}
              headerUnderline={false}
              popupSubLabel="Are you sure you want to delete this coach?"
              className="body-scroll-lock height-full delete-update"
              popupLabel="Delete coach"
              setVisible={setShowDeletePopup}
              visible={showDeletePopup}
              align="centre"
              btnLabel1="Cancel"
              btnLabel2="Delete"
              handlebtn2Click={handleDeleteCoachList}
              handlebtn1Click={() => {
                setShowDeletePopup(false);
              }}
            />
          </GridWrapper>
        </CardWrapper>
      )}
    </>
  );
};
export default CoachesTable;
const GridWrapper = styled.div`
  .k-grid-container {
    height: calc(100vh - 255px);
  }
  .k-table-thead {
    .k-link {
      cursor: default;
    }
    .coach-header-style,
    .team-header-style {
      cursor: pointer !important;
      .k-link {
        cursor: pointer !important;
      }
    }
  }
  .k-grid-content tr .coach-name-style {
    .name-label,
    .sub-name-label {
      cursor: pointer !important;
    }
  }
`;
